import type { Project } from "./state"
import { DIMENSIONS, type Dimension } from "./dimensions"

// A phase is a top-level UX mode — what kind of work the user is doing
// right now. Phase scopes the menu's "what's relevant" set and the chrome's
// headline affordance. It's a UX primitive, not a prompt-budget optimization;
// tools and prompt remain unchanged in this iteration.
//
// Phase has both a derived value (what the project state suggests) and a
// chosen value (what the user explicitly picked). The chosen value lives on
// LoomDoc.phase as Phase | null; null means "follow derivation."
export type Phase = "fabrics" | "blocks" | "blanket" | "finishing"

export const PHASES: readonly Phase[] = ["fabrics", "blocks", "blanket", "finishing"] as const

export const PHASE_LABELS: Record<Phase, string> = {
  fabrics: "Fabrics",
  blocks: "Blocks",
  blanket: "Blanket",
  finishing: "Finishing",
}

// Derivation: first-match wins, mirroring the user's working order.
//   no fabrics            → fabrics  (nothing to design with yet)
//   fabrics but no layout → blocks   (designing the block before placing it)
//   layout sized          → blanket  (instantiating + coloring blocks)
// "finishing" is opt-in only — the user explicitly tells the machine "I'm
// done designing, show me the totals." Derivation never lands there because
// editing is unbounded.
export function derivePhase(p: Project): Phase {
  if (p.fabrics.length === 0) return "fabrics"
  if (p.layout.rows === 0 || p.layout.cols === 0) return "blocks"
  return "blanket"
}

// Resolve the active phase: chosen overrides derived. Used by the menu
// renderer, the phase strip, and any per-phase chrome.
export function resolvePhase(p: Project, chosen: Phase | null | undefined): Phase {
  return chosen ?? derivePhase(p)
}

// Map a dimension's category to the phase that owns it. Today's categories
// are block / layout / palette / color / fabric — only block and layout
// have registered dimensions, but the table is exhaustive so adding a
// new dimension category fails to compile here rather than silently
// landing in the wrong phase.
const CATEGORY_PHASE: Record<Dimension["category"], Phase> = {
  block:   "blocks",
  layout:  "blanket",
  palette: "blanket",
  color:   "blocks",
  fabric:  "fabrics",
}

// Dimensions relevant to the given phase. Used by the menu to filter the
// shared registry; the per-dimension submenu structure means each filtered
// dimension becomes its own sub-menu row at the phase's root.
export function dimensionsForPhase(phase: Phase): Dimension[] {
  return DIMENSIONS.filter((d) => CATEGORY_PHASE[d.category] === phase)
}
